import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { projectsApi } from '../../api/projects.api';
import type { Site, CreateSiteInput, SiteStatus } from '../../api/sites.api';

interface Props {
  open: boolean;
  initial?: Site | null;
  onClose: () => void;
  onSubmit: (input: CreateSiteInput) => Promise<void>;
  error?: string | null;
}

const STATUSES: SiteStatus[] = ['active', 'inactive', 'completed', 'closed'];

export function SiteFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const { data: projects } = useQuery({ queryKey: ['projects', 'all'], queryFn: () => projectsApi.list({ limit: 100 }) });

  const [form, setForm] = useState<CreateSiteInput>({ projectId: 0, siteName: '', status: 'active' });

  useEffect(() => {
    setForm(
      initial
        ? {
            projectId: initial.projectId,
            siteName: initial.siteName,
            address: initial.address ?? undefined,
            latitude: initial.latitude ?? undefined,
            longitude: initial.longitude ?? undefined,
            status: initial.status,
          }
        : { projectId: 0, siteName: '', status: 'active' },
    );
  }, [initial, open]);

  const set = <K extends keyof CreateSiteInput>(k: K, v: CreateSiteInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? `Edit Site — ${initial?.siteCode}` : 'Create Site'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            select
            label="Project"
            value={form.projectId || ''}
            onChange={(e) => set('projectId', Number(e.target.value))}
            disabled={isEdit}
            helperText={isEdit ? 'Project cannot be changed after creation' : 'Client is derived from the project'}
            required
            fullWidth
          >
            {(projects?.data ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.projectName} ({p.projectCode})</MenuItem>
            ))}
          </TextField>
          <TextField label="Site Name" value={form.siteName} onChange={(e) => set('siteName', e.target.value)} required fullWidth />
          <TextField label="Address" value={form.address ?? ''} onChange={(e) => set('address', e.target.value)} fullWidth multiline rows={2} />
          <Stack direction="row" spacing={2}>
            <TextField label="Latitude" type="number" value={form.latitude ?? ''} onChange={(e) => set('latitude', e.target.value ? Number(e.target.value) : undefined)} fullWidth />
            <TextField label="Longitude" type="number" value={form.longitude ?? ''} onChange={(e) => set('longitude', e.target.value ? Number(e.target.value) : undefined)} fullWidth />
          </Stack>
          <TextField select label="Status" value={form.status} onChange={(e) => set('status', e.target.value as SiteStatus)} fullWidth>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)} disabled={!form.projectId}>{isEdit ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
}
